import { legalAddressLines, legalConfig } from '../config/legal';
import { legalPages, resolveLegalPage, type LegalPage, type LegalSection } from '../config/legal-content';
import { element } from './dom';

export const LEGAL_PATHS = new Set([...Object.keys(legalPages), '/refunds']);

export function legalPageHeader(): HTMLElement {
  const header=element('header',{className:'legal-header'});
  const link=element('a',{className:'legal-home-link',attrs:{href:'/','aria-label':'Return to Alunar home'}});
  link.append(element('span',{className:'legal-wordmark',text:'ALUNAR'}),element('span',{className:'legal-back',text:'Back to Alunar'}));
  header.append(link);
  return header;
}

export function legalPageFooter(): HTMLElement {
  const footerNode=element('footer',{className:'legal-footer'});
  const nav=element('nav',{attrs:{'aria-label':'Legal page navigation'}});
  const links:Array<[string,string]>=[['Privacy Policy','/privacy'],['Terms and Conditions','/terms'],['Refund Policy','/refund-policy'],['Privacy Settings','/privacy-settings'],['Alunar home','/']];
  links.forEach(([label,href])=>nav.append(element('a',{text:label,attrs:{href}})));
  const email=element('a',{text:legalConfig.contactEmail,attrs:{href:`mailto:${legalConfig.contactEmail}`}});
  const contact=element('p',{text:'Contact: '});contact.append(email);
  footerNode.append(nav,element('p',{text:`© 2026 ${legalConfig.brandAttribution}`}),contact);
  return footerNode;
}

export function legalPageLayout(content: HTMLElement, labelledBy: string): HTMLElement {
  const page=element('section',{className:'screen legal legal-page',attrs:{'aria-labelledby':labelledBy}});
  const shell=element('div',{className:'shell legal-shell'});shell.append(content);
  page.append(legalPageHeader(),shell,legalPageFooter());
  return page;
}

export function legalView(path:string):HTMLElement {
  const page=resolveLegalPage(path)??legalPages['/privacy'];
  const article=element('article',{className:'legal-article'});
  article.append(element('p',{className:'legal-kicker',text:legalConfig.brandName}),element('h1',{text:page.title,attrs:{id:'legal-title'}}),dateBlock(),element('p',{className:'legal-opening',text:page.opening}),tableOfContents(page));
  page.sections.forEach((section,index)=>article.append(renderSection(section,index+1)));
  return legalPageLayout(article,'legal-title');
}

function dateBlock():HTMLElement {
  const block=element('dl',{className:'legal-dates'});
  block.append(element('dt',{text:'Effective date'}),element('dd',{text:legalConfig.effectiveDate}),element('dt',{text:'Last updated'}),element('dd',{text:legalConfig.lastUpdated}));
  return block;
}

function tableOfContents(page:LegalPage):HTMLElement {
  const aside=element('nav',{className:'legal-toc',attrs:{'aria-label':`${page.title} contents`}});
  aside.append(element('h2',{text:'Contents'}));const list=element('ol');
  page.sections.forEach((section,index)=>{const item=element('li');item.append(element('a',{text:section.heading,attrs:{href:`#legal-section-${index+1}`}}));list.append(item)});
  aside.append(list);return aside;
}

function renderSection(section:LegalSection,index:number):HTMLElement {
  const wrapper=element('section',{className:'legal-section',attrs:{'aria-labelledby':`legal-section-${index}`}});
  wrapper.append(element('h2',{text:`${index}. ${section.heading}`,attrs:{id:`legal-section-${index}`}}));
  section.paragraphs?.forEach(copy=>wrapper.append(element('p',{text:copy})));
  if(section.bullets?.length){const list=element('ul');section.bullets.forEach(copy=>list.append(element('li',{text:copy})));wrapper.append(list)}
  section.links?.forEach(link=>{const anchor=element('a',{className:'legal-external-link',text:link.label,attrs:{href:link.href,...(link.external?{target:'_blank',rel:'noopener noreferrer'}:{})}});wrapper.append(anchor)});
  if(section.contact)wrapper.append(legalContactBlock());
  return wrapper;
}

export function legalContactBlock():HTMLElement {
  const address=element('address',{className:'legal-contact'});
  address.append(element('strong',{text:legalConfig.brandName}),element('span',{text:`Operated by ${legalConfig.legalEntity}`}),element('span',{text:legalConfig.entityDescription}),element('span',{text:'Business mailing address:'}));
  legalAddressLines.forEach(line=>address.append(element('span',{text:line})));
  const email=element('a',{text:legalConfig.contactEmail,attrs:{href:`mailto:${legalConfig.contactEmail}`}});const emailLine=element('span',{text:'Email: '});emailLine.append(email);address.append(emailLine);
  return address;
}

export function footer(onPrivacy:()=>void):HTMLElement {
  const footerNode=element('footer',{className:'footer'});const nav=element('nav',{attrs:{'aria-label':'Legal'}});
  const links:Array<[string,string]>=[['Privacy','/privacy'],['Terms','/terms'],['Refunds','/refund-policy'],['Cookies','/cookies'],['Disclaimer','/disclaimer']];
  links.forEach(([label,href])=>nav.append(element('a',{text:label,attrs:{href}})));
  nav.append(element('button',{className:'text-button',text:'Privacy Settings',attrs:{type:'button'},on:{click:onPrivacy}}));
  const email=element('a',{text:legalConfig.contactEmail,attrs:{href:`mailto:${legalConfig.contactEmail}`}});
  footerNode.append(nav,element('p',{text:`© 2026 ${legalConfig.brandAttribution}`}),email);return footerNode;
}
